import { messageFromError } from '@oysterjs/core/graphql/errors';
import { formatTime } from '~/lib/format-time';

/** A failed mailbox save or send, read from the server's error and ready to show. */
export type MailboxActionError = {
  message: string;
  /** The agent already sent this email from their mail client, and the server recorded it. */
  sentExternally: boolean;
  /** The mailbox needs reconnecting, so its connection status may have changed. */
  requiresAccountRefresh: boolean;
};

type MailboxErrorExtensions = {
  mailboxErrorKind?: unknown;
  sentExternally?: unknown;
  sentExternallyAt?: unknown;
};

const extensionsOf = (error: unknown): MailboxErrorExtensions[] => {
  const errors = (error as { graphQLErrors?: unknown } | undefined)?.graphQLErrors;
  return Array.isArray(errors)
    ? errors.map((item) => (item as { extensions?: MailboxErrorExtensions })?.extensions ?? {})
    : [];
};

// Generic application codes conflate consent failures with item-level denials.
// Only mailbox errors that can change connection status warrant another query.
export const requiresMailboxRefresh = (error: unknown): boolean =>
  extensionsOf(error).some(
    (extensions) =>
      extensions.mailboxErrorKind === 'reauth_required' ||
      extensions.mailboxErrorKind === 'consent_required'
  );

// Error extensions sit outside the schema, so codegen leaves them untyped and
// the send time arrives as the server's RFC 3339 string.
const sentExternallyAtOf = (extensions: MailboxErrorExtensions[]): Date | null => {
  for (const { sentExternallyAt } of extensions) {
    if (typeof sentExternallyAt !== 'string') continue;
    const sentAt = new Date(sentExternallyAt);
    if (!Number.isNaN(sentAt.getTime())) return sentAt;
  }
  return null;
};

/**
 * The server's own message names the send time in UTC; this one names it in
 * the reader's time zone.
 */
export const alreadySentExternallyMessage = (
  mailClient: string,
  sentAt: Date | null,
  timeZone?: string
): string =>
  sentAt
    ? `This email was already sent from ${mailClient} at ${formatTime(sentAt, timeZone)}.`
    : `This email was already sent from ${mailClient}.`;

export const mailboxActionError = (
  error: unknown,
  fallback: string,
  mailClient: string
): MailboxActionError => {
  const extensions = extensionsOf(error);
  const sentExternally = extensions.some((item) => item.sentExternally === true);
  return {
    message: sentExternally
      ? alreadySentExternallyMessage(mailClient, sentExternallyAtOf(extensions))
      : messageFromError(error, fallback),
    sentExternally,
    requiresAccountRefresh: requiresMailboxRefresh(error)
  };
};
